// GLOBAL SPRITE CACHE FOR HEAL EFFECT
const healSpriteCache = new Map<string, HTMLCanvasElement>();

function getHealBubbleCoreSprite(size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `bubble-core-${qSize}`;
    let sprite = healSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = qSize * 2;
        sprite.height = qSize * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = qSize;
            const centerY = qSize;
            const coreGrad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            // Bake opacity at 1.0, use globalAlpha in render
            coreGrad.addColorStop(0, `rgba(255, 255, 255, 1)`);
            coreGrad.addColorStop(0.5, `rgba(134, 239, 172, 0.8)`);
            coreGrad.addColorStop(1, `rgba(34, 197, 94, 0)`);

            sCtx.fillStyle = coreGrad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        healSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getHealBubbleStrandSprite(width: number, height: number): HTMLCanvasElement {
    const qW = Math.max(1, Math.round(width));
    const qH = Math.max(1, Math.round(height));
    const key = `bubble-strand-${qW}-${qH}`;
    let sprite = healSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        // Needs extra width for the x-offset of 15 when drawing the ellipse
        // Actually it's drawn at (15, 0) internally
        // We can just draw it centered and translate 15 in render
        sprite.width = qW * 2;
        sprite.height = qH * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            sCtx.fillStyle = `rgba(253, 224, 71, 0.5)`; // Base color and opacity
            sCtx.beginPath();
            sCtx.ellipse(qW, qH, qW, qH, 0, 0, Math.PI * 2);
            sCtx.fill();
        }
        healSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getHealBubbleShellSprite(size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `bubble-shell-${qSize}`;
    let sprite = healSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = qSize * 2;
        sprite.height = qSize * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = qSize;
            const centerY = qSize;
            // The shell has an offset glow center (cx - bubbleSize * 0.3)
            const offX = centerX - qSize * 0.3;
            const offY = centerY - qSize * 0.3;

            const grad = sCtx.createRadialGradient(offX, offY, qSize * 0.1, centerX, centerY, qSize);
            grad.addColorStop(0, `rgba(255, 255, 255, 0.8)`);
            grad.addColorStop(0.2, `rgba(134, 239, 172, 0.2)`);
            grad.addColorStop(0.8, `rgba(34, 197, 94, 0.4)`);
            grad.addColorStop(1, `rgba(255, 255, 255, 0.6)`);

            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        healSpriteCache.set(key, sprite);
    }
    return sprite;
}

export type HealBeam = {
    angle: number
    dist: number
    speed: number
    width: number
    length: number
    layer: number
    flyAwaySpeed: number
}

export type HealDroplet = {
    x: number
    y: number
    vx: number
    vy: number
    size: number
    elasticity: number
    active: boolean
}

export type HealData = {
    beams: HealBeam[]
    droplets: HealDroplet[]
}

export type HealEffect = {
    kind: 'heal'
    t: number
    x: number
    y: number
    w: number
    h: number
    data: HealData
}

/**
 * Initializes and spawns a Heal effect
 * @param x Target x coordinate
 * @param y Target y coordinate
 * @param w Target width (default 0)
 * @param h Target height (default 0)
 * @returns The initialized effect object
 */
export function createHealEffect(x: number, y: number, w = 0, h = 0): HealEffect {
    const cx = x + w / 2
    const cy = y + h / 2
    const beams: HealBeam[] = []
    const droplets: HealDroplet[] = []

    // 1. Beams (Layer 1 - Standard)
    for (let k = 0; k < 20; k++) {
        const angle = Math.random() * Math.PI * 2
        beams.push({
            angle,
            dist: 450 + Math.random() * 300,
            speed: 6 + Math.random() * 4.5,
            width: 3 + Math.random() * 3,
            length: 300 + Math.random() * 150,
            layer: 1,
            flyAwaySpeed: 0
        })
    }
    // 1. Beams (Layer 2 - Bigger, Wider)
    for (let k = 0; k < 15; k++) {
        const angle = Math.random() * Math.PI * 2
        beams.push({
            angle,
            dist: 525 + Math.random() * 375,
            speed: 4.5 + Math.random() * 4.5,
            width: 6 + Math.random() * 4.5,
            length: 375 + Math.random() * 180,
            layer: 2,
            flyAwaySpeed: 0
        })
    }

    // 2. Droplets
    for (let k = 0; k < 60; k++) {
        const angle = Math.random() * Math.PI * 2
        const speed = 3 + Math.random() * 9
        droplets.push({
            x: cx,
            y: cy,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed - 7.5,
            size: 3 + Math.random() * 4.5,
            elasticity: 0.5 + Math.random() * 0.3,
            active: false
        })
    }

    return {
        kind: 'heal',
        t: 0,
        x, y, w, h,
        data: { beams, droplets }
    }
}

/**
 * Updates and renders the Heal effect
 * @param effect The effect object to render
 * @param ctx The canvas rendering context
 * @returns true if the effect should continue, false if it should be removed
 */
export function renderHealEffect(
    effect: HealEffect,
    ctx: CanvasRenderingContext2D
): boolean {
    const maxTime = 3000
    const tt = effect.t / maxTime
    if (tt > 1.0) return false

    const cx = effect.x + (effect.w || 0) / 2
    const cy = effect.y + (effect.h || 0) / 2
    const floorY = effect.y + (effect.h || 0)

    const data = effect.data
    ctx.save()

    // --- Phase 1: Accumulation (0.0 - 0.6) ---
    // Beams converge, Bubble grows

    const phase1Limit = 0.6
    const isPhase1 = tt < phase1Limit

    // Draw Beams
    ctx.globalCompositeOperation = 'screen'

    data.beams.forEach((b) => {
        if (isPhase1) {
            // Converging
            b.dist -= b.speed
            if (b.dist < 15) b.dist = 15 // 1.5x (was 10)
        } else {
            // Phase 2: Fly Away
            if (b.flyAwaySpeed === 0) {
                b.flyAwaySpeed = 7.5 + Math.random() * 15 // 1.5x (was 5+10)
                // Reverse angle slightly or just continue through?
                // "flying away to different sides" implies movement.
                // Let's make them expand OUTWARD from center.
                b.dist = 15 // Reset to center
                b.angle += Math.PI // Fly opposite direction (pass through)
            }
            b.dist += b.flyAwaySpeed
        }

        // Opacity Gradient Logic
        // "more closer to avatar up to 0 transparency, more further ... 100%"
        // Wait, "0 transparency" means OPAQUE. "100% transparency" means INVISIBLE.
        // So: Center = Opaque, Outer = Transparent.

        const beamAlpha = isPhase1 ? Math.min(1, (tt / phase1Limit) * 2) : Math.max(0, 1 - (tt - phase1Limit) * 3)

        if (beamAlpha > 0.01) {
            const bx = cx + Math.cos(b.angle) * b.dist
            const by = cy + Math.sin(b.angle) * b.dist
            const ex = cx + Math.cos(b.angle) * (b.dist + b.length)
            const ey = cy + Math.sin(b.angle) * (b.dist + b.length)

            const grad = ctx.createLinearGradient(bx, by, ex, ey)
            grad.addColorStop(0, `rgba(253, 224, 71, ${beamAlpha})`) // Center (Opaque-ish)
            grad.addColorStop(1, `rgba(253, 224, 71, 0)`) // Outer (Transparent)

            ctx.strokeStyle = grad
            ctx.lineWidth = b.width
            ctx.lineCap = 'round'

            ctx.beginPath()
            ctx.moveTo(bx, by)
            ctx.lineTo(ex, ey)
            ctx.stroke()
        }
    })

    // Draw Bubble (Only in Phase 1 & start of 2)
    if (tt < 0.7) {
        const bubbleScale = isPhase1 ? (tt / phase1Limit) : (1 + (tt - phase1Limit) * 2) // Pop expansion
        const bubbleAlpha = isPhase1 ? 1 : (1 - (tt - phase1Limit) * 5) // Fade out fast on pop

        if (bubbleAlpha > 0) {
            const bubbleSize = (60 + Math.sin(effect.t * 0.02) * 7.5) * bubbleScale // 1.5x (was 40 + ... * 5)

            // Core: "multilayer moving and glowing core"
            const coreSize = bubbleSize * 0.6
            ctx.globalAlpha = bubbleAlpha;
            const coreSprite = getHealBubbleCoreSprite(coreSize);
            ctx.drawImage(coreSprite, cx - coreSize, cy - coreSize);

            // Moving inner strands/core
            ctx.save()
            ctx.translate(cx, cy)
            ctx.rotate(effect.t * 0.005)
            // Use alpha scaling for strands
            ctx.globalAlpha = bubbleAlpha;
            const strandW = coreSize * 0.8;
            const strandH = coreSize * 0.3;
            const strandSprite = getHealBubbleStrandSprite(strandW, strandH);

            for (let r = 0; r < 3; r++) {
                ctx.rotate(Math.PI * 2 / 3)
                // Draw sprite offset by 15 in X
                ctx.drawImage(strandSprite, 15 - strandW, 0 - strandH);
            }
            ctx.restore()

            // Main Bubble Shell
            ctx.globalAlpha = bubbleAlpha;
            const shellSprite = getHealBubbleShellSprite(bubbleSize);
            ctx.drawImage(shellSprite, cx - bubbleSize, cy - bubbleSize);
        }
    }

    // --- Phase 2: Splash (0.6 - 1.0) ---
    if (!isPhase1) {
        // Activate droplets
        if (!data.droplets[0].active) {
            data.droplets.forEach((d) => d.active = true)
        }

        const splashT = (tt - phase1Limit) / (1 - phase1Limit)
        ctx.globalAlpha = 1 - splashT

        // Droplets
        const gravity = 0.6
        data.droplets.forEach((d) => {
            if (d.active) {
                d.vy += gravity
                d.x += d.vx
                d.y += d.vy

                if (d.y > floorY) {
                    d.y = floorY
                    d.vy *= -d.elasticity
                    d.vx *= 0.8
                }

                ctx.beginPath()
                ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2)
                ctx.fillStyle = 'rgba(134, 239, 172, 0.9)'
                ctx.fill()
            }
        })
    }

    ctx.restore()

    return true
}
